// Angular
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
// AG-Grid
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Router } from '@angular/router';
import { StateModel } from '../model/state-status.model';
import { OrderInboxService } from '../service/order-inbox.service';
import { BroadcastEventModel, BroadcastService, ToasterType } from '@cv-invision/core';
import { RESUBMIT_ORDER_EVENT } from '../resubmit-order/resubmit-order.component';
import { Store } from '@ngxs/store';
import { INVALID_DATA_ORDER } from '../model/invalid-data-order-state.model';
import { Subject, takeUntil } from 'rxjs';
import { CleanQualityOrder } from '../../quality/state/actions/quality.actions';

@Component({
	selector: 'cv-custom-action-cell',
	templateUrl: './custom-action-cell.component.html',
	styleUrls: ['./custom-action-cell.component.scss']
})
export class CustomActionCellComponent implements OnInit, OnDestroy, ICellRendererAngularComp {
	cellValue: any;
	dataCell: any;
	canReopen: boolean;
	canResubmit: boolean;
	clientReferenceId: number;
	actionsDisabled = false;
	loadingOrder = false;

	private atComponentDestroy = new Subject();

	constructor(
		private ngZone: NgZone,
		private router: Router,
		private store: Store,
		private inboxService: OrderInboxService,
		private broadcastService: BroadcastService
	) { }

	ngOnInit(): void {
		this.loadingOrder = false;
		this.broadcastService
			.onEvent(INVALID_DATA_ORDER)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.loadingOrder = false;
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * When the component renders, it got the possible value
	 * @param params
	 */
	agInit(params: ICellRendererParams): void {
		const { data } = params;
		this.cellValue = params;
		if (data.stateEnum === StateModel.Approved && data.schema !== 'InspectionReport:3') {
			this.canReopen = true;
		}
		this.canResubmit = data.stateEnum === StateModel.Rejected;
		this.clientReferenceId = data.externalId;
		if (data.smartQCState === 1) {
			this.actionsDisabled = true;
		}
	}

	/**
	 * Put back the value
	 * @param params
	 */
	refresh(params: ICellRendererParams): boolean {
		this.cellValue = params;
		return true;
	}

	/**
	 * open rejection box
	 */
	toggleReSubmitBox(): void {
		if (!this.canResubmit || this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		this.broadcastService.broadcast({
			name: RESUBMIT_ORDER_EVENT,
			event: {
				isResubmit: true,
				data: data
			}
		});
	}

	/**
	 * re opnen an order
	 */
	reOpen(): void {
		if (!this.canReopen || this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		this.inboxService.reOpenOrder(data.id).subscribe(response => {
			if (response.isValid) {
				this.broadcastService.broadcast({
					name: ToasterType.SUCCESS,
					event: {
						message: `The Order <span class="order-id">${data.id} </span> was successfully reopened`
					}
				});
				this.ngZone.run(() => {
					setTimeout(() => {
						this.router.navigate([`/intelligence/quality/initial`, data.id]);
					}, 500);
				});
			}
		});
	}

	/**
	 * Returns a name initials (firstname + lastname)
	 */
	getNameInitials(): any {
		const lockedBy = this.cellValue.data.lockedBy;
		return lockedBy ? `${lockedBy.firstName.charAt(0).toUpperCase()}${lockedBy.lastName.charAt(0).toUpperCase()}` : '';
	}

	/**
	 * Returns lock by title
	 */
	getLockTitle(): string {
		if (this.cellValue.data.locked && this.cellValue.data.lockedBy) {
			return `Locked by ${this.cellValue.data.lockedBy.firstName} ${this.cellValue.data.lockedBy.lastName}`;
		}
		return '';
	}

	/**
	 * redirect to the selected order detail page
	 */
	openOrder(): void {
		if (this.actionsDisabled) {
			return;
		}
		const { data } = this.cellValue;
		if (data && !this.loadingOrder) {
			this.loadingOrder = true;
			this.ngZone.run(async () => {
				await this.store.dispatch(new CleanQualityOrder()).toPromise();
				setTimeout(async () => {
					this.router.navigate([`/intelligence/quality/initial`, data.id]);
				}, 50);
			});
		}
	}
}
